import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { HttpCacheInterceptorModule, useHttpCacheLocalStorage } from '@ngneat/cashew';
import { EdAssistUIFooterModule } from '@edassist-ui/footer';
import { EdAssistUIGlobalAlertsModule } from '@edassist-ui/global-alerts';
import { EdAssistUISessionTimeoutModalModule } from '@edassist-ui/session-timeout';
import { AppConfig, AppConfigService, AppTitleService, WINDOW_PROVIDERS } from '@edassist/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
	ApiEndpointInterceptor,
	APP_CONFIG,
	ErrorInterceptor,
	HttpCredentialsInterceptor,
	HttpHeadersInterceptor,
	QueueRequestInterceptor
} from '@edassist/api';
import { SliderService } from '@edassist-ui/services';
import { EdAssistUINavbarTopHeaderModule } from './shared/components/tuition-navbar-top-header';
import { NotificationsModule } from '@edassist/common/components/notifications/notifications.module';
import { CookieService } from 'ngx-cookie-service';
import { GoogleTagManagerConfiguration, GoogleTagManagerModule } from 'angular-google-tag-manager';
import { NgIdleModule } from '@ng-idle/core';
import { ExternalNavigationModalModule } from '@edassist/common/components/external-navigation-modal/external-navigation-modal.module';
import { EdMarketPlaceSurveyModule } from './modules/ed-market-place-survey/ed-market-place-survey.module';
import { AuthClientConfig, AuthModule } from '@auth0/auth0-angular';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

// tslint:disable-next-line
export function initializeApp(
	appConfig: AppConfig,
	gtmConfig: GoogleTagManagerConfiguration,
	authClientConfig: AuthClientConfig
) {
	return () => {
		AppConfigService.settings = appConfig;
		if (AppConfigService.settings.env.gtmContainerId && gtmConfig) {
			gtmConfig.set({ id: AppConfigService.settings.env.gtmContainerId });
		}
		authClientConfig.set({
			domain: AppConfigService.settings.auth0?.auth0Issuer ?? '',
			clientId: AppConfigService.settings.auth0?.auth0ClientId ?? '',
			issuer: AppConfigService.settings.auth0?.auth0Issuer ?? '',
			cacheLocation: 'localstorage',
			useRefreshTokens: true,
			authorizationParams: {
				audience: AppConfigService.settings.auth0?.auth0Audience ?? '',
				redirect_uri: `${window.location.origin}/tuition`,
				scope: 'offline_access openid profile email'
			}
		});
	};
}

@NgModule({
	declarations: [AppComponent],
	imports: [
		BrowserModule,
		AppRoutingModule,
		EdAssistUIFooterModule,
		EdAssistUIGlobalAlertsModule,
		EdAssistUINavbarTopHeaderModule,
		EdAssistUISessionTimeoutModalModule,
		ExternalNavigationModalModule,
		EdMarketPlaceSurveyModule,
		BrowserAnimationsModule,
		AuthModule.forRoot(),
		NgIdleModule.forRoot(),
		HttpClientModule,
		HttpCacheInterceptorModule.forRoot({ ttl: 900000 }),
		NotificationsModule,
		GoogleTagManagerModule.forRoot(),
		MatProgressSpinner
	],
	providers: [
		AppConfigService,
		AppTitleService,
		WINDOW_PROVIDERS,
		{
			provide: APP_INITIALIZER,
			useFactory: initializeApp,
			deps: [APP_CONFIG, GoogleTagManagerConfiguration, AuthClientConfig],
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: ApiEndpointInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpCredentialsInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpHeadersInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: QueueRequestInterceptor,
			multi: true
		},
		{ provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
		SliderService,
		CookieService,
		useHttpCacheLocalStorage
	],
	bootstrap: [AppComponent]
})
export class AppModule {}
